import StorageService from '@/services/storage.service'
import storageKeys from '@/constants/storageKeys'
import { defaultFilters } from '@/models/Players/helpers'
import playersRepository from '@/repository/generatedRepository/playersRepository'
import TableOptions from '@/models/TableOptions'
import { moveFiltersToCustom, omitEmptyDeepFilters, prepareFilters } from '@/helpers/filters'
import { canFitInLocalStorage } from '@/helpers/reports'
import playerReportsRepository from "@/repository/generatedRepository/playerReportsRepository";
import { noDataPagination } from '@/helpers/render-value'

// sent as custom_filter[...] (see moveFiltersToCustom)
const CUSTOM_FILTER_KEYS = ['retargeting']

const state = {
  list: [],
  filters: StorageService.get(storageKeys.PLAYERS_FILTERS) || { ...defaultFilters() },
  tableOptions: TableOptions.defaultTableOptions(),
}

const getters = {
  filters: state => state.filters,
  tableOptions: state => state.tableOptions,
  list: state => state.list,
  isOpenFilterPanel: state =>
    state.filters.clientOptions && state.filters.clientOptions.filterPanelExpanded,
}

const actions = {
  async loadData(context, payload) {
    const {signal, updateData = true, ...filters} = payload
    context.commit('setFilters', filters)
    StorageService.set(storageKeys.PLAYERS_FILTERS, filters)

    const requestFilters = moveFiltersToCustom(filters, CUSTOM_FILTER_KEYS)
    const appliedFilters = omitEmptyDeepFilters(requestFilters.filter)
    const appliedCfFilters = omitEmptyDeepFilters(filters.cfFilter)

    const preparedFilters = prepareFilters({
      ...requestFilters,
      filter: appliedFilters,
      cfFilter: appliedCfFilters,
      signal,
      isFilter: 1,
    })

    let data = {}
    if (!updateData) {
      data = StorageService.get(storageKeys.PLAYERS_DATA) || noDataPagination
    } else {
      data = (await playersRepository.list(preparedFilters)).data
    }

    if (canFitInLocalStorage(data)) {
      StorageService.set(storageKeys.PLAYERS_DATA, data)
    } else {
      StorageService.remove(storageKeys.PLAYERS_DATA)
    }

    context.commit('setList', { data, filters })

    return { products: data.items, pagination: data.pagination }
  },
  async loadExportFile(context, filters) {
    const requestFilters = moveFiltersToCustom(filters, CUSTOM_FILTER_KEYS)
    const appliedFilters = omitEmptyDeepFilters(requestFilters.filter)
    const preparedFilters = prepareFilters({
      ...requestFilters,
      filter: appliedFilters,
    })
    const { data } = await playersRepository.exportPlayersReportToken({...preparedFilters})
    window.location.href = process.env.VUE_APP_REPORTS_API_ORIGIN + data.url
  },
  async loadPlayerReports(context, filter) {
    const { data } = await playerReportsRepository.list(filter)
    return data
  },
  async searchPlayers(context, filters) {
    const requestFilters = moveFiltersToCustom(filters, CUSTOM_FILTER_KEYS)
    const appliedFilters = omitEmptyDeepFilters(requestFilters.filter)
    const preparedFilters = prepareFilters({
      ...requestFilters,
      filter: appliedFilters,
    })

    const { data } = await playersRepository.list(preparedFilters)

    return { products: data.items, pagination: data.pagination }
  },
  async loadItem(context, id) {
    const { data } = await playersRepository.get(id)
    return data
  },
  bulkUpdate(context, payload) {
    const appliedFilters = omitEmptyDeepFilters(payload.params)
    const preparedFilters = prepareFilters({
      ...payload,
    params: {
        pagination: { limit: -1 },
        filter: appliedFilters,
    }
    })
    return playersRepository.bulkUpdate(preparedFilters)
  },
  createItem(context, payload) {
    return playersRepository.post(payload)
  },
  updateItem(context, payload) {
    const { id, ...data } = payload
    return playersRepository.put(id, data)
  },
  deleteItem(context, payload) {
    return playersRepository.delete(payload)
  },
  filtersStorageReset() {
    StorageService.remove(storageKeys.PLAYERS_FILTERS)
  },
  resetSearchFilter({commit}){
    commit('clearSearch')
  },
  updateFiltersClientOptions({ commit, getters }, clientOptions) {
    const updatedFilters = {
      ...getters.filters,
      clientOptions,
    }
    commit('setFilters', updatedFilters)
    StorageService.set(storageKeys.PLAYERS_FILTERS, updatedFilters)
  },
}

const mutations = {
  setList(state, { data, filters }) {
    state.list = [...data.items]
    state.tableOptions = new TableOptions(data.pagination, filters)
  },
  setFilters(state, filters) {
    state.filters = { ...filters }
  },
  clearSearch(state) {
    state.tableOptions.search = ''
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
